/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import { useReducedMotion } from 'motion/react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { usePersistentValue, usePrevious } from '@gitbook/util-react-hooks';

import { type SidePanelHost, SidePanelExpandableContext, SidePanelHostContext } from './context';

export interface PageWithSidePanelProps {
    /** Main content always displayed. */
    children: React.ReactNode;
    /** Side-content to render, null if it should be closed. */
    sidePanel: React.ReactNode | null;
}

/**
 * Panel transition to be used in motion/react, duration is in seconds.
 *
 * One timing for everything that moves the frame around — the sidebar, a side panel, the rail —
 * because they move together: a panel opening shrinks the page from the right as the sidebar
 * collapsing widens it from the left, and two edges moving on different clocks read as a wobble.
 */
export function usePanelTransition() {
    const shouldReduceMotion = useReducedMotion();
    return {
        duration: shouldReduceMotion ? 0 : 0.15,
        ease: [0.4, 0, 0.2, 1],
    };
}

/**
 * Panel transition to be used in CSS.
 */
export function usePanelTransitionStyle() {
    const transition = usePanelTransition();
    return {
        transitionDuration: `${transition.duration}s`,
        transitionTimingFunction: `cubic-bezier(${transition.ease.join(',')})`,
    };
}

/**
 * Component to render a main body with a side-panel on the right.
 *
 * The panel lies over the right of the page by default, and the page gives up the width it covers.
 * Where the layout offers a host through {@link SidePanelHostContext} — a column it already keeps
 * aside — the panel is rendered into that column instead: the page keeps all of its width, and the
 * host decides how much of the column the panel gets and what shares it. Only one
 * `PageWithSidePanel` may use a host at a time, since they would otherwise render into each other.
 */
export function PageWithSidePanel(props: PageWithSidePanelProps): React.ReactElement {
    const { children, sidePanel } = props;

    const host = React.useContext(SidePanelHostContext);
    const isChangingSurface = useIsChangingSurface(host);
    const [isExpanded, setExpanded] = React.useState(false);
    const expandableContext = React.useMemo(() => ({ isExpanded, setExpanded }), [isExpanded]);
    const transitionStyle = usePanelTransitionStyle();
    const persistentSidePanel = usePersistentValue(sidePanel);
    const isOpened = Boolean(sidePanel);
    const { isAnimating, animatedProps } = useIsAnimating({ isOpened });

    return (
        <SidePanelExpandableContext.Provider value={expandableContext}>
            <div
                className={clsx(
                    'flex min-h-0 flex-1 overflow-x-hidden overflow-y-auto',
                    '[--panel-padding:0.375rem]',
                    isExpanded
                        ? '[--panel-width:34rem]'
                        : '[--panel-width:22rem] max-sm:[--panel-width:100vw]',
                    // Every variable the application owns is read with a fallback, so the panel
                    // holds its shape outside the app frame too — in Storybook, where none of them
                    // are defined, it would otherwise lose its width cap and its bottom edge.
                    '[--max-panel-width:min(var(--panel-width),calc(100vw-var(--app-layout-spacing-unit,0.375rem)*2))]',
                    // A host reserves the panel's width itself, so there is nothing for the page to
                    // give up here.
                    isOpened && !host
                        ? 'sm:pr-[calc(var(--panel-width)+var(--panel-padding))]'
                        : null
                )}
                // The page gives up its width smoothly when a panel opens over it — but not when
                // the panel is only changing surface, where the width it gives up changes all at
                // once and there is nothing to follow: the panel itself jumps between the page and
                // the column rather than travelling, so animating the page around it reads as the
                // page moving on its own.
                style={{
                    ...transitionStyle,
                    transitionProperty: isChangingSurface ? 'none' : 'padding',
                }}
            >
                <div
                    className="md:app-framing-styles relative flex flex-1 flex-col"
                    // Allow the parent AppLayout to detect whether a child screen is rendering with sidepanels, to skip framing styles. See app.css for more.
                    data-side-panel-frame
                >
                    {children}
                </div>
                {host ? null : (
                    <div
                        className={clsx(
                            'absolute top-1.5 right-1.5 z-10 flex flex-col overflow-hidden rounded-xl border-0.5 border-control bg-base shadow-subtle',
                            'bottom-[var(--app-bottom-unavailable,0.375rem)] max-sm:top-[var(--app-top-unavailable,0.375rem)]',
                            'w-screen max-w-(--max-panel-width)',
                            isOpened
                                ? null
                                : clsx(
                                      'pointer-events-none translate-x-[calc(var(--max-panel-width)+var(--panel-padding))]',
                                      isAnimating ? null : 'invisible'
                                  )
                        )}
                        // Both named, because Tailwind's translate utilities set the `translate`
                        // property rather than `transform`: named alone, `transform` never changes,
                        // the slide never runs, and the panel lands in place while the page is still
                        // shrinking around it — overlapping it for the whole of the page's move.
                        style={{ ...transitionStyle, transitionProperty: 'transform, translate' }}
                        {...animatedProps}
                    >
                        {isAnimating || isOpened ? persistentSidePanel : null}
                    </div>
                )}
            </div>
            {/* Rendered into the host only while there is a panel, and taken straight back out
                when there is not: a host holding nothing has to be genuinely empty for the layout
                to hand its space to whatever else shares the column. That is why this one neither
                lingers nor slides out the way the overlay above does — an exit would put the space
                back only once a transition ended, which a browser is free not to run at all (in a
                background tab, say), leaving the column split around a panel nobody can see.

                Nothing is rendered while the column is still being attached, rather than falling
                back to the overlay for that one render — see {@link SidePanelHost}. */}
            {host?.element && isOpened
                ? createPortal(
                      <div
                          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border-0.5 border-control bg-base shadow-subtle"
                          // Read by the host's layout, which widens the column for a panel the user
                          // has expanded.
                          data-side-panel-expanded={isExpanded || undefined}
                      >
                          {sidePanel}
                      </div>,
                      host.element
                  )
                : null}
        </SidePanelExpandableContext.Provider>
    );
}

/**
 * Whether the panel is moving between the page and a host on this render.
 *
 * True for the one paint the move takes, and false again straight after — the flag has to reach the
 * DOM together with the change it is suppressing, so it is cleared from an effect that runs after
 * the browser has painted rather than before it.
 */
function useIsChangingSurface(host: SidePanelHost | null): boolean {
    const [painted, setPainted] = React.useState(host);
    const isChanging = painted !== host;

    React.useEffect(() => {
        if (isChanging) {
            setPainted(host);
        }
    }, [isChanging, host]);

    return isChanging;
}

/**
 * Check if the element is animating / transitioning.
 */
function useIsAnimating(props: { isOpened: boolean }) {
    const { isOpened } = props;
    const shouldReduceMotion = useReducedMotion();
    const [isAnimating, setIsAnimating] = React.useState(false);
    const previousIsOpened = usePrevious(isOpened);
    React.useLayoutEffect(() => {
        if (
            previousIsOpened !== undefined &&
            previousIsOpened !== isOpened &&
            !shouldReduceMotion
        ) {
            setIsAnimating(true);
        }
    }, [previousIsOpened, isOpened, shouldReduceMotion]);
    return {
        isAnimating,
        animatedProps: {
            onTransitionEnd: (event: React.TransitionEvent) => {
                if (event.target === event.currentTarget) {
                    setIsAnimating(false);
                }
            },
        },
    };
}
